/**
 * REPORT AND BLOCK — the client half.
 *
 * The server half has been live and tested since public games shipped
 * (`backend/src/safety.js`, `backend/tests/safety.test.js`). Blocks are
 * ENFORCED at the door: `findBlockConflict` runs on every join to a
 * stranger-visible room and on every Quick Match candidate, symmetrically, so
 * a block cannot be routed around by the person you blocked.
 *
 * None of which mattered, because until now there was no way for a human being
 * to create one. This file is the missing wire.
 *
 * Nothing in here throws for the caller to ignore: every mutation returns a
 * discriminated result, because the call sites are all "a parent is upset and
 * pressing a button" and a silent failure there is the worst possible outcome.
 */

import { API_BASE } from './apiBase';
import { firebaseAuth } from './firebase';

/**
 * The reasons, in the words a person would actually use.
 *
 * The wire values are the server's (`REPORT_REASONS` in safety.js) and must not
 * drift — an unrecognised reason is silently coerced to `other`, which would
 * quietly throw away the one piece of information the report is FOR.
 *
 * The labels are deliberately plain. A ten-year-old is a plausible user of this
 * menu, and "inappropriate_content" is not a thing a ten-year-old says.
 */
export const REPORT_REASONS = [
  { value: 'offensive_name', label: 'Their name is rude' },
  { value: 'offensive_language', label: 'They wrote something rude' },
  { value: 'harassment', label: 'They are being mean to someone' },
  { value: 'inappropriate_content', label: "They wrote something that isn't for kids" },
  { value: 'cheating', label: 'They are cheating' },
  { value: 'other', label: 'Something else' },
] as const;

export type ReportReason = (typeof REPORT_REASONS)[number]['value'];

/** Where the report was filed from. Mirrors the server's `REPORT_CONTEXTS`. */
export type ReportContext = 'game' | 'thread' | 'name' | 'profile';

export interface BlockedPlayer {
  targetUid: string;
  createdAt?: { _seconds?: number } | string | number | null;
}

export interface ReportInput {
  targetUid: string;
  reason: ReportReason;
  contextType: ReportContext;
  /** The room code / game id / thread id this happened in, when we know it. */
  contextId?: string | null;
  /** Free text. Optional on purpose — requiring an essay stops people filing. */
  note?: string | null;
}

export class SafetyError extends Error {
  code: string;
  status: number;

  constructor(code: string, status: number) {
    super(code);
    this.name = 'SafetyError';
    this.code = code;
    this.status = status;
  }

  /**
   * What we actually put in front of the person. Never the raw code — they are
   * pressing this button on a bad day and do not need `http-429`.
   */
  get userMessage(): string {
    if (this.status === 429) return "You've sent a lot of reports today. Take a break — we've got the earlier ones.";
    if (this.status === 401 || this.status === 403) return 'Sign in first, then you can report or block.';
    if (this.code === 'no-api-base') return "We can't reach the server right now.";
    return "That didn't send. Try again in a moment.";
  }
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (!API_BASE) throw new SafetyError('no-api-base', 0);

  const token = await firebaseAuth.getCurrentUserToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers || {}),
    },
  });

  const body = await res.json().catch(() => null);
  if (!res.ok) throw new SafetyError((body as any)?.error || `http-${res.status}`, res.status);
  return body as T;
}

/**
 * File a report. The server takes the reporter from the verified token and
 * ignores anything we might say about who we are, which is why there is no
 * reporterUid parameter here.
 */
export async function reportUser(input: ReportInput): Promise<{ ok: true; id: string }> {
  return api<{ ok: true; id: string }>('/reports', {
    method: 'POST',
    body: JSON.stringify({
      targetUid: input.targetUid,
      reason: input.reason,
      contextType: input.contextType,
      contextId: input.contextId ?? null,
      note: input.note?.trim() ? input.note.trim() : null,
    }),
  });
}

export async function blockUser(targetUid: string): Promise<void> {
  await api('/blocks', { method: 'POST', body: JSON.stringify({ targetUid }) });
}

export async function unblockUser(targetUid: string): Promise<void> {
  await api(`/blocks/${encodeURIComponent(targetUid)}`, { method: 'DELETE' });
}

/** Everyone I have blocked. A bare array — the route does not wrap it. */
export async function listBlocks(): Promise<BlockedPlayer[]> {
  const rows = await api<BlockedPlayer[]>('/blocks');
  return Array.isArray(rows) ? rows : [];
}
